import { TooltipV2, type TooltipPlacement } from "@/components/TooltipV2";
import { classNames } from "@/utils/classnames";
import React from "react";

interface TruncatedTextWithTooltipWhenTruncatedBaseProps {
  children?: React.ReactNode;
  className?: string;
  noArrow?: boolean;
  placement?: TooltipPlacement;
  showTooltip?: boolean;
  value: string;
}

type TruncatedTextWithTooltipWhenTruncatedTooltipProps =
  | { overrideTooltip?: never; tooltip?: React.ReactNode }
  | { overrideTooltip?: React.ReactNode; tooltip?: never };

export type TruncatedTextWithTooltipWhenTruncatedProps =
  TruncatedTextWithTooltipWhenTruncatedBaseProps &
    TruncatedTextWithTooltipWhenTruncatedTooltipProps;

/** Reveals the complete value when text is clipped by its own box or its table cell. */
export const TruncatedTextWithTooltipWhenTruncated: React.FC<
  TruncatedTextWithTooltipWhenTruncatedProps
> = ({
  children,
  className,
  noArrow,
  overrideTooltip,
  placement = "top",
  showTooltip = true,
  tooltip,
  value,
}) => {
  const visibleValueRef = React.useRef<HTMLSpanElement>(null);
  const hasCustomTooltip = tooltip != null || overrideTooltip != null;

  const textIsTruncated = React.useCallback(() => {
    const visibleValue = visibleValueRef.current;
    if (visibleValue == null) return false;
    if (
      visibleValue.scrollWidth > visibleValue.clientWidth ||
      visibleValue.scrollHeight > visibleValue.clientHeight
    )
      return true;

    // Tall rows clamp an outer cell wrapper while the wrapping text keeps its natural height.
    const cell = visibleValue.closest("td");
    if (cell == null) return false;
    for (
      let ancestor = visibleValue.parentElement;
      ancestor != null;
      ancestor = ancestor.parentElement
    ) {
      const { overflowY } = getComputedStyle(ancestor);
      if (
        (overflowY === "hidden" || overflowY === "clip") &&
        ancestor.scrollHeight > ancestor.clientHeight
      )
        return true;
      if (ancestor === cell) break;
    }
    return false;
  }, []);

  return (
    <TooltipV2
      content={
        overrideTooltip ?? (
          <div
            className={
              "min-w-0 max-w-[420px] select-text whitespace-pre-wrap [overflow-wrap:anywhere]"
            }
          >
            <div>{value}</div>
            {tooltip != null && <div className={"mt-cds-4"}>{tooltip}</div>}
          </div>
        )
      }
      disabled={!showTooltip}
      noArrow={noArrow}
      placement={placement}
      shouldOpen={hasCustomTooltip ? undefined : textIsTruncated}
    >
      <span
        ref={visibleValueRef}
        className={classNames("block min-w-0 max-w-full truncate", className)}
      >
        {children ?? value}
      </span>
    </TooltipV2>
  );
};
